
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../API/axiosNeatex';
import type { APIResponse } from '@store/Shared/shared';

export interface ProjectCostItem {
  id: string;
  organizationId: string;
  taskId: string | null;
  logicalLineId: string | null;
  sourceDocumentId: string;
  sourceType: string;
  amount: number;
  currency: string;
  date: string;
  statusId: string;
  notes: string;
}

export const addProjectCostItem = createAsyncThunk('Project/addProjectCostItem', async (item: any, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.post<APIResponse<string>>('Project/AddProjectCostItem', item);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const updateProjectCostItem = createAsyncThunk('Project/updateProjectCostItem', async (item: any, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.post('Project/UpdateProjectCostItem', item);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const deleteProjectCostItem = createAsyncThunk('Project/deleteProjectCostItem', async (id: string, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.post('Project/DeleteProjectCostItem', { id });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getCostItemsByOrg = createAsyncThunk('Project/getCostItemsByOrg', async (organizationId: string, { rejectWithValue }) => {
  try {
    const { data } = await ntxAPI.get(`Project/GetCostItemsByOrg?organizationId=${organizationId}`);
    if (data.succeeded === true) {
      return data.data;
    } else {
      return [];
    }
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

const initialState: {
  costItems: ProjectCostItem[];
  previousCostItems: ProjectCostItem[];
  loading: boolean;
  error: any | null;
} = {
  costItems: [],
  previousCostItems: [],
  loading: false,
  error: null,
};

const projectCostSlice = createSlice({
  name: 'ProjectCost',
  initialState,
  reducers: {
    updateCostItemInList: (state, action: PayloadAction<{ costItemId: string; updates: Partial<ProjectCostItem> }>) => {
      const { costItemId, updates } = action.payload;
      const idx = state.costItems.findIndex(c => String(c.id) === String(costItemId));
      if (idx !== -1) {
        state.costItems[idx] = { ...state.costItems[idx], ...updates };
      }
    },
    rollbackCostItems: state => {
      state.costItems = state.previousCostItems;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getCostItemsByOrg.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCostItemsByOrg.fulfilled, (state, action: PayloadAction<ProjectCostItem[]>) => {
        state.loading = false;
        state.costItems = action.payload;
        state.previousCostItems = action.payload;
      })
      .addCase(getCostItemsByOrg.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { updateCostItemInList, rollbackCostItems } = projectCostSlice.actions;
export default projectCostSlice.reducer;